import React from 'react';
import { getPositionInDecade, getDecadeForBead, OurFatherPhase } from '../../utils/rosaryMapping';

interface RosaryFooterCounterProps {
  currentBeadIndex: number;
  currentStepIndex: number;
  totalSteps: number;
  ourFatherPhase: OurFatherPhase;
  prayerType: string;
  className?: string;
}

const FINAL_MEDAL_INDEX = 60;

export const RosaryFooterCounter: React.FC<RosaryFooterCounterProps> = ({
  currentBeadIndex,
  currentStepIndex,
  totalSteps,
  ourFatherPhase,
  prayerType,
  className = ''
}) => {
  // Determine which mode to render
  const isIntro = currentBeadIndex >= 0 && currentBeadIndex <= 4;
  const isBead5Intro = currentBeadIndex === 5 && currentStepIndex <= 3;
  const isFinal = currentBeadIndex === FINAL_MEDAL_INDEX;
  const isDecade = !isIntro && !isBead5Intro && !isFinal && getDecadeForBead(currentBeadIndex) !== null;
  // Bead 5 in decade mode (on decade-1 step)
  const isBead5Decade = currentBeadIndex === 5 && currentStepIndex > 3 && !isFinal;

  if (isIntro || isBead5Intro) {
    return <IntroCounter currentBeadIndex={currentBeadIndex} className={className} />;
  }

  if (isDecade || isBead5Decade) {
    return <DecadeCounter currentBeadIndex={currentBeadIndex} ourFatherPhase={ourFatherPhase} className={className} />;
  }

  if (isFinal) {
    return <FinalCounter currentStepIndex={currentStepIndex} ourFatherPhase={ourFatherPhase} prayerType={prayerType} className={className} />;
  }

  return null;
};

// --- Intro Counter (beads 0-5: Apostles' Creed, Our Father, 3 Hail Marys, Glory Be) ---

const IntroCounter: React.FC<{ currentBeadIndex: number; className: string }> = ({ currentBeadIndex, className }) => {
  let prayerLabel: string;
  let progressText: string;

  if (currentBeadIndex === 0) {
    prayerLabel = "Apostles' Creed";
    progressText = 'Opening Prayers';
  } else if (currentBeadIndex === 1) {
    prayerLabel = 'Our Father';
    progressText = 'Opening Prayers';
  } else if (currentBeadIndex >= 2 && currentBeadIndex <= 4) {
    const hailMaryNumber = currentBeadIndex - 1;
    const virtues = ['Faith', 'Hope', 'Charity'];
    prayerLabel = `Hail Mary for ${virtues[hailMaryNumber - 1]}`;
    progressText = `${hailMaryNumber} of 3`;
  } else {
    prayerLabel = 'Glory Be';
    progressText = 'Announce Mysteries';
  }

  const filledClass = 'bg-indigo-600 border-indigo-600 dark:bg-indigo-400 dark:border-indigo-400';
  const activeClass = `${filledClass} ring-1 ring-indigo-300 dark:ring-indigo-500`;
  const emptyClass = 'bg-indigo-200 border-indigo-300 dark:bg-indigo-800 dark:border-indigo-700';
  const emptySmallClass = 'bg-indigo-100 border-indigo-200 dark:bg-indigo-900 dark:border-indigo-700';

  const dotState = (bead: number) =>
    currentBeadIndex > bead ? filledClass : currentBeadIndex === bead ? activeClass : emptyClass;

  const smallDotState = (bead: number) =>
    currentBeadIndex > bead ? filledClass : currentBeadIndex === bead ? `bg-indigo-500 border-indigo-500 dark:bg-indigo-300 dark:border-indigo-300 ring-1 ring-indigo-300 dark:ring-indigo-500` : emptySmallClass;

  const isGloryBe = currentBeadIndex === 5;

  return (
    <div className={`flex flex-col items-center gap-1 ${className}`}>
      <div className="flex items-center justify-between w-full">
        <span className={`text-xs font-medium ${isGloryBe ? 'text-amber-700 dark:text-amber-300' : 'text-indigo-700 dark:text-indigo-300'}`}>{prayerLabel}</span>
        <span className={`text-xs ${isGloryBe ? 'text-amber-600 dark:text-amber-400' : 'text-indigo-600 dark:text-indigo-400'}`}>{progressText}</span>
      </div>
      <div className="flex justify-center gap-1">
        {/* Apostles' Creed (cross shape, bead 0) */}
        <div className={`relative w-2.5 h-3 flex items-center justify-center transition-colors ${currentBeadIndex === 0 ? 'opacity-100' : currentBeadIndex > 0 ? 'opacity-100' : 'opacity-40'}`}>
          <div className={`absolute w-0.5 h-3 rounded-sm ${currentBeadIndex >= 0 ? (currentBeadIndex === 0 ? 'bg-indigo-600 dark:bg-indigo-400' : 'bg-indigo-600 dark:bg-indigo-400') : 'bg-indigo-300 dark:bg-indigo-700'}`} />
          <div className={`absolute w-2 h-0.5 rounded-sm -mt-1 ${currentBeadIndex >= 0 ? (currentBeadIndex === 0 ? 'bg-indigo-600 dark:bg-indigo-400' : 'bg-indigo-600 dark:bg-indigo-400') : 'bg-indigo-300 dark:bg-indigo-700'}`} />
          {currentBeadIndex === 0 && <div className="absolute inset-0 bg-indigo-400 dark:bg-indigo-500 rounded-sm opacity-30 blur-[2px] scale-150" />}
        </div>
        {/* Our Father (bead 1) */}
        <div className={`w-2.5 h-2.5 rounded-full border-2 transition-colors ${dotState(1)}`} />
        {/* 3 Hail Marys (beads 2-4) */}
        {[2, 3, 4].map(bead => (
          <div key={bead} className={`w-1.5 h-1.5 rounded-full border transition-colors ${smallDotState(bead)}`} />
        ))}
        {/* Glory Be (bead 5) */}
        <div className={`w-2.5 h-2.5 rounded-full border-2 transition-colors ${
          currentBeadIndex === 5
            ? 'bg-amber-500 border-amber-500 dark:bg-amber-400 dark:border-amber-400 ring-1 ring-amber-300 dark:ring-amber-500'
            : emptyClass
        }`} />
      </div>
    </div>
  );
};

// --- Decade Counter (beads 5-59, decade range) ---

const DecadeCounter: React.FC<{ currentBeadIndex: number; ourFatherPhase: OurFatherPhase; className: string }> = ({ currentBeadIndex, ourFatherPhase, className }) => {
  const position = getPositionInDecade(currentBeadIndex);
  if (!position) return null;

  let prayerLabel: string;
  let progressText: string;

  if (position.isOurFather) {
    if (ourFatherPhase === 'closing') {
      prayerLabel = 'Glory Be & Fatima Prayer';
      progressText = position.decade === 1 ? 'End of Introduction' : `End of Decade ${position.decade - 1}`;
    } else {
      prayerLabel = 'Our Father';
      progressText = `Decade ${position.decade}`;
    }
  } else {
    prayerLabel = 'Hail Mary';
    progressText = `${position.hailMaryNumber} of 10 · Decade ${position.decade}`;
  }

  const isClosing = position.isOurFather && ourFatherPhase === 'closing';

  return (
    <div className={`flex flex-col items-center gap-1 ${className}`}>
      <div className="flex items-center justify-between w-full">
        <span className={`text-xs font-medium ${isClosing ? 'text-amber-700 dark:text-amber-300' : 'text-indigo-700 dark:text-indigo-300'}`}>{prayerLabel}</span>
        <span className={`text-xs ${isClosing ? 'text-amber-600 dark:text-amber-400' : 'text-indigo-600 dark:text-indigo-400'}`}>{progressText}</span>
      </div>
      <div className="flex justify-center gap-0.5">
        {/* Our Father (large) */}
        <div className={`w-2.5 h-2.5 rounded-full border-2 transition-colors ${
          position.isOurFather && ourFatherPhase === 'opening'
            ? 'bg-indigo-600 border-indigo-600 dark:bg-indigo-400 dark:border-indigo-400 ring-1 ring-indigo-300 dark:ring-indigo-500'
            : 'bg-indigo-200 border-indigo-300 dark:bg-indigo-800 dark:border-indigo-700'
        }`} />
        {/* 10 Hail Marys */}
        {Array.from({ length: 10 }, (_, i) => (
          <div key={i} className={`w-1.5 h-1.5 rounded-full border transition-colors ${
            !position.isOurFather && position.hailMaryNumber! > i + 1
              ? 'bg-indigo-600 border-indigo-600 dark:bg-indigo-400 dark:border-indigo-400'
              : !position.isOurFather && position.hailMaryNumber! === i + 1
              ? 'bg-indigo-500 border-indigo-500 dark:bg-indigo-300 dark:border-indigo-300 ring-1 ring-indigo-300 dark:ring-indigo-500'
              : 'bg-indigo-100 border-indigo-200 dark:bg-indigo-900 dark:border-indigo-700'
          }`} />
        ))}
        {/* Glory Be (large) */}
        <div className={`w-2.5 h-2.5 rounded-full border-2 transition-colors ${
          position.isOurFather && ourFatherPhase === 'closing'
            ? 'bg-amber-500 border-amber-500 dark:bg-amber-400 dark:border-amber-400 ring-1 ring-amber-300 dark:ring-amber-500'
            : 'bg-indigo-200 border-indigo-300 dark:bg-indigo-800 dark:border-indigo-700'
        }`} />
      </div>
    </div>
  );
};

// --- Final Prayers Counter (bead 60) ---

const FinalCounter: React.FC<{ currentStepIndex: number; ourFatherPhase: OurFatherPhase; prayerType: string; className: string }> = ({ currentStepIndex, ourFatherPhase, prayerType, className }) => {
  const isNovena = prayerType === '54-day-novena';

  const hailHolyQueenStep = 9;
  const rosaryPrayerStep = 10;
  const closingPrayerStep = isNovena ? 11 : -1;
  const reflectionStep = isNovena ? 12 : 11;
  const completeStep = isNovena ? 13 : 12;

  let prayerLabel: string;
  let progressText: string;
  let currentPrayerIndex: number;

  if (ourFatherPhase === 'closing') {
    prayerLabel = 'Glory Be & Fatima Prayer';
    progressText = 'End of 5th Decade';
    currentPrayerIndex = 0;
  } else if (currentStepIndex === hailHolyQueenStep) {
    prayerLabel = 'Hail Holy Queen';
    progressText = 'Closing Prayers';
    currentPrayerIndex = 1;
  } else if (currentStepIndex === rosaryPrayerStep) {
    prayerLabel = 'Rosary Prayer';
    progressText = 'Closing Prayers';
    currentPrayerIndex = 2;
  } else if (isNovena && currentStepIndex === closingPrayerStep) {
    prayerLabel = 'Closing Prayer';
    progressText = 'Novena Closing';
    currentPrayerIndex = 3;
  } else if (currentStepIndex === reflectionStep) {
    prayerLabel = 'Reflection';
    progressText = 'Journal Entry';
    currentPrayerIndex = isNovena ? 4 : 3;
  } else if (currentStepIndex === completeStep) {
    prayerLabel = 'Complete!';
    progressText = 'Prayer Finished';
    currentPrayerIndex = isNovena ? 5 : 4;
  } else {
    prayerLabel = 'Closing';
    progressText = `Step ${currentStepIndex + 1}`;
    currentPrayerIndex = 0;
  }

  const dots = [
    { label: 'Glory Be & Fatima', index: 0 },
    { label: 'Hail Holy Queen', index: 1 },
    { label: 'Rosary Prayer', index: 2 },
    ...(isNovena ? [{ label: 'Closing Prayer', index: 3 }] : []),
    { label: 'Reflection', index: isNovena ? 4 : 3 },
    { label: 'Complete', index: isNovena ? 5 : 4 },
  ];

  return (
    <div className={`flex flex-col items-center gap-1 ${className}`}>
      <div className="flex items-center justify-between w-full">
        <span className="text-xs font-medium text-amber-700 dark:text-amber-300">{prayerLabel}</span>
        <span className="text-xs text-amber-600 dark:text-amber-400">{progressText}</span>
      </div>
      <div className="flex justify-center gap-1">
        {dots.map((dot, i) => {
          const isLast = i === dots.length - 1;
          const isActive = currentPrayerIndex === dot.index;
          const isPast = currentPrayerIndex > dot.index;
          return (
            <div
              key={dot.label}
              className={`${isLast ? 'w-2.5 h-2.5 border-2' : 'w-2 h-2 border'} rounded-full transition-colors ${
                isPast || isActive
                  ? isLast && isActive
                    ? 'bg-green-500 border-green-500 dark:bg-green-400 dark:border-green-400 ring-1 ring-green-300 dark:ring-green-500'
                    : isActive
                    ? 'bg-amber-500 border-amber-500 dark:bg-amber-400 dark:border-amber-400 ring-1 ring-amber-300 dark:ring-amber-500'
                    : 'bg-amber-500 border-amber-500 dark:bg-amber-400 dark:border-amber-400'
                  : 'bg-amber-100 border-amber-200 dark:bg-amber-900 dark:border-amber-700'
              }`}
              title={dot.label}
            />
          );
        })}
      </div>
    </div>
  );
};
